import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Activity, Search, RefreshCw, Clock, User, FileText } from 'lucide-react';
import { jobsAPI } from '../services/api';
import { theme } from '../styles/theme';
import { MetricTile, MetricTileGrid } from '../components/MetricTile';
import { extractApiError } from '../utils/errorMessages';
import { useToast } from '../hooks/use-toast';

interface Job {
  id: string;
  filename: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  user: {
    id: number;
    username: string;
    email: string;
  };
  total_rows?: number;
  error_message?: string;
}

const ActivityLogs: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Redirect non-admin users
  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadJobs();
  }, [searchQuery, statusFilter]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const response = await jobsAPI.list();
      const jobsData = (response.data as any).results || response.data;
      let allJobs: Job[] = Array.isArray(jobsData) ? jobsData : [];
      
      // Filter by search query
      if (searchQuery) {
        allJobs = allJobs.filter((job: Job) => 
          job.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
          job.user?.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          job.user?.email?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      // Filter by status
      if (statusFilter !== 'all') {
        allJobs = allJobs.filter((job: Job) => job.status === statusFilter);
      }
      
      // Sort by created_at descending (newest first)
      allJobs.sort((a: Job, b: Job) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      setJobs(allJobs);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast({ title: extractApiError(error, "Couldn't load activity"), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      pending: { color: 'bg-qc-warning/10 text-qc-warning', label: 'Pending' },
      processing: { color: 'bg-interactive-primary/10 text-interactive-primary', label: 'Processing' },
      completed: { color: 'bg-qc-success/10 text-qc-success', label: 'Completed' },
      failed: { color: 'bg-qc-error/10 text-qc-error', label: 'Failed' },
      cancelled: { color: 'bg-bg-subtle text-text-primary', label: 'Cancelled' },
    };
    
    const config = statusConfig[status] || { color: 'bg-bg-subtle text-text-primary', label: status };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getDuration = (startDate: string, endDate: string | null) => {
    if (!endDate) return 'N/A';
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const diffSeconds = Math.floor((end - start) / 1000);
    
    if (diffSeconds < 60) return `${diffSeconds}s`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ${diffSeconds % 60}s`;
    return `${Math.floor(diffSeconds / 3600)}h ${Math.floor((diffSeconds % 3600) / 60)}m`;
  };

  const stats = {
    total: jobs.length,
    completed: jobs.filter(j => j.status === 'completed').length,
    processing: jobs.filter(j => j.status === 'processing').length,
    failed: jobs.filter(j => j.status === 'failed').length,
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back Button */}
      <Link 
        to="/admin" 
        className={`inline-flex items-center ${theme.text.secondary} hover:text-interactive-primary-hover mb-4 transition`}
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Admin Dashboard
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="sr-only">Activity Logs</h2>
        <button
          onClick={loadJobs}
          className="px-6 py-3 bg-qc-success hover:bg-qc-success/90 text-text-inverse font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
        >
          <RefreshCw className="w-5 h-5" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats */}
      <MetricTileGrid columns={4} className="mb-6">
        <MetricTile label="Total Jobs" value={stats.total} IconComponent={Activity} />
        <MetricTile label="Completed" value={stats.completed} IconComponent={Clock} />
        <MetricTile label="Processing" value={stats.processing} IconComponent={Activity} />
        <MetricTile label="Failed" value={stats.failed} IconComponent={Activity} />
</MetricTileGrid>

      {/* Filters */}
      <div className={`${theme.card.background} rounded-lg shadow p-6 mb-6`}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary w-5 h-5" />
              <input
                type="text"
                placeholder="Search by file name, username, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              />
            </div>
          </div>
          <div className="w-full md:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-interactive-primary"></div>
        </div>
      ) : jobs.length === 0 ? (
        <div className={`${theme.card.background} rounded-lg shadow p-12 text-center`}>
          <Activity className="w-16 h-16 mx-auto text-text-tertiary mb-4" />
          <p className={`${theme.text.secondary} text-lg`}>No activity logs found</p>
        </div>
      ) : (
        <div className={`${theme.card.background} rounded-lg shadow overflow-hidden`}>
          <table className={`min-w-full divide-y ${theme.border.primary.replace('border-', 'divide-')}`}>
            <thead className={`${theme.background.primary}`}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  File Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Rows
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className={`${theme.card.background} divide-y divide-border`}>
              {jobs.map((job) => (
                <tr key={job.id} className="hover:bg-bg-subtle">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-text-tertiary mr-2" />
                      <div>
                        <div className={`text-sm font-medium ${theme.text.primary}`}>
                          {job.user?.username || 'Unknown'}
                        </div>
                        <div className="text-xs text-text-tertiary">
                          {job.user?.email || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 text-text-tertiary mr-2" />
                      <span className={`text-sm ${theme.text.primary}`}>
                        {job.filename}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(job.status)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.text.primary}`}>
                    {job.total_rows || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-tertiary">
                    {formatDate(job.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-tertiary">
                    {getDuration(job.created_at, job.completed_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;
